import React, { useContext, useMemo } from 'react';
import clsx from 'clsx';
import type {
  RenderSupplementaryNavItemsProps, SupplementaryNavigationProps,
} from '../../../types';
import { HeaderContext } from '../../context';
import NavigationLink from '../NavigationLink';
import { dispatchNavigationHotjarEvent, excludeNonRelevantNavItemsPerAudience } from '../helpers';
import { getFromLocalStorage, LS_KEYS } from '../../../../../helpers/window';

const RenderSupplementaryNavItems = ({ navigationItems }: RenderSupplementaryNavItemsProps) => (
  <ul className="pd-ml-4 pd-flex pd-items-center pd-gap-4">
    {navigationItems.map((navigationItem) => (
      <li key={navigationItem.id} className="pd-hidden lg:first:pd-list-item xl:pd-list-item">
        <NavigationLink
          iconBefore={navigationItem.icon}
          to={navigationItem.linkedEntry?.path || navigationItem.path}
          onMouseEnter={dispatchNavigationHotjarEvent}
          navigationType="supplementary"
          eventLabel={navigationItem.title}
          className="pd-flex pd-text-sm"
        >
          {navigationItem.title}
        </NavigationLink>
      </li>
    ))}
  </ul>
);

const SupplementaryNavigation = ({
  navigation,
  className,
}: SupplementaryNavigationProps) => {
  /**
   * Populated the header context with audience data, to avoid prop drilling
   */
  const {
    isSticky,
    audiencesDataFromSegment,
    audienceOverRideForPlayground,
  } = useContext(HeaderContext);
  const emulatedAudienceType = audienceOverRideForPlayground;
  const customerAudienceType = emulatedAudienceType
    || getFromLocalStorage(LS_KEYS.AUDIENCE_TYPE) || audiencesDataFromSegment?.type;

  /**
   * The character length of each items should be <=8
   * for maximum of 4 items for supplementary navigation
   */
  const slicedNavItems = useMemo(
    () => excludeNonRelevantNavItemsPerAudience(navigation.items?.slice(0, 5), customerAudienceType) || [],
    [navigation.items, customerAudienceType],
  );

  if (!slicedNavItems.length) {
    return null;
  }

  return (
    <section className={clsx(isSticky && 'pd-hidden', className)} data-testid="desktop-supplementary-navigation">
      <RenderSupplementaryNavItems navigationItems={slicedNavItems} />
    </section>
  );
};

export default SupplementaryNavigation;
